import { Box, Container, Grid, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import AppFooter from "../Shared/AppFooter/AppFooter";
import AppHeader from "../Shared/AppHeader/AppHeader";
import aboutUsStyles from "./AboutUs.styles";

const AboutUs = () => {
  const classes = aboutUsStyles;
  const { t } = useTranslation();

  // ── Hero ────────────────────────────────────────────────────────────────
  const heroChips = [
    { emoji: "🌟", labelKey: "aboutUsChip1" },
    { emoji: "🔭", labelKey: "aboutUsChip2" },
    { emoji: "💡", labelKey: "aboutUsChip3" },
  ];

  const getHeroSection = () => (
    <Box sx={classes.heroWrapper}>
      <Box sx={classes.heroGlowTop} />
      <Box sx={classes.heroGlowBottom} />
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box sx={classes.heroBadge}>
            <Box sx={classes.heroBadgeDot} />
            <Typography sx={classes.heroBadgeText}>{t("aboutUsBadge")}</Typography>
          </Box>
        </Box>
        <Typography sx={classes.heroTitle}>{t("aboutUsHeroTitle")}</Typography>
        <Box sx={classes.heroAccentBar} />
        <Typography sx={classes.heroSubtitle}>{t("aboutUsHeroSubtitle")}</Typography>
        <Box sx={classes.heroChipsRow}>
          {heroChips.map((chip) => (
            <Box key={chip.labelKey} sx={classes.heroChip}>
              <Typography sx={classes.heroChipEmoji}>{chip.emoji}</Typography>
              <Typography sx={classes.heroChipText}>{t(chip.labelKey)}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  // ── Project / About content ────────────────────────────────────────────
  const bullets = [
    t("aboutUsBullet1"),
    t("aboutUsBullet2"),
    t("aboutUsBullet3"),
    t("aboutUsBullet4"),
    t("aboutUsBullet5"),
  ];

  const getProjectSection = () => (
    <Box>
      <Box sx={classes.sectionLabelBox}>
        <Typography sx={classes.sectionLabel}>{t("aboutUsProjectLabel")}</Typography>
      </Box>
      <Typography sx={classes.sectionTitle}>{t("aboutUsSectionProject")}</Typography>
      <Box sx={classes.sectionBar} />

      {/* Lead paragraph — large, prominent opening */}
      <Typography sx={classes.leadParagraph}>{t("aboutUsP1")}</Typography>

      {/* Mission — left-border callout */}
      <Box sx={classes.missionBox}>
        <Typography sx={classes.missionLabel}>{t("aboutUsMissionLabel")}</Typography>
        <Typography sx={classes.missionText}>{t("aboutUsP2")}</Typography>
      </Box>

      {/* Body paragraphs */}
      <Typography sx={classes.bodyParagraph}>{t("aboutUsP3")}</Typography>
      <Typography sx={classes.bodyParagraph}>{t("aboutUsP4")}</Typography>

      {/* Bullet list */}
      <Typography sx={classes.bulletsIntro}>{t("aboutUsP5")}</Typography>
      <Box sx={{ pl: 0.5 }}>
        {bullets.map((b, i) => (
          <Box key={i} sx={classes.bulletRow}>
            <Box sx={classes.bulletIconBox}>
              <Box sx={classes.bulletIcon} />
            </Box>
            <Typography sx={classes.bulletText}>{b}</Typography>
          </Box>
        ))}
      </Box>

      {/* Continued body */}
      <Typography sx={[classes.bodyParagraph, { mt: 3 }]}>{t("aboutUsP6")}</Typography>

      {/* Pioneering closing callout */}
      <Box sx={classes.pioneerBox}>
        <Typography sx={classes.pioneerStar}>✦ ✦ ✦</Typography>
        <Typography sx={classes.pioneerText}>{t("aboutUsP7")}</Typography>
      </Box>
    </Box>
  );

  // ── Platform Capabilities ─────────────────────────────────────────────
  const capabilities = [
    { num: "01", icon: "✨", titleKey: "aboutUsCap1Title", descKey: "aboutUsCap1Desc" },
    { num: "02", icon: "🔗", titleKey: "aboutUsCap2Title", descKey: "aboutUsCap2Desc" },
    { num: "03", icon: "🔮", titleKey: "aboutUsCap3Title", descKey: "aboutUsCap3Desc" },
    { num: "04", icon: "🌐", titleKey: "aboutUsCap4Title", descKey: "aboutUsCap4Desc" },
  ];

  const getCapabilitiesSection = () => (
    <Box>
      <Box sx={classes.sectionLabelBox}>
        <Typography sx={classes.sectionLabel}>{t("aboutUsPlatformSub")}</Typography>
      </Box>
      <Typography sx={classes.sectionTitle}>{t("aboutUsPlatformTitle")}</Typography>
      <Box sx={classes.sectionBar} />
      <Grid container spacing={3} mt={0.5} alignItems="stretch">
        {capabilities.map((cap) => (
          <Grid item xs={12} sm={6} key={cap.titleKey} sx={{ display: "flex" }}>
            <Box sx={classes.capCard}>
              <Box sx={classes.capCardHeader}>
                <Box sx={classes.capIconBox}>{cap.icon}</Box>
                <Typography sx={classes.capNumber}>{cap.num}</Typography>
              </Box>
              <Typography sx={classes.capTitle}>{t(cap.titleKey)}</Typography>
              <Typography sx={classes.capDesc}>{t(cap.descKey)}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // ── Legal / Company ───────────────────────────────────────────────────
  const legalRows = [
    { labelKey: "aboutUsAddressLabel", valueKey: "aboutUsAddressValue" },
    { labelKey: "aboutUsRegLabel", valueKey: "aboutUsRegValue" },
    { labelKey: "aboutUsVatLabel", valueKey: "aboutUsVatValue" },
  ];

  const getLegalSection = () => (
    <Box>
      <Box sx={classes.sectionLabelBox}>
        <Typography sx={classes.sectionLabel}>{t("aboutUsLegalTitle")}</Typography>
      </Box>
      <Typography sx={classes.sectionTitle}>{t("aboutUsLegalSectionTitle")}</Typography>
      <Box sx={classes.sectionBar} />
      <Box sx={classes.legalCard}>
        <Box sx={classes.legalCardGlow} />
        <Typography sx={classes.legalCompanyName}>{t("aboutUsCompanyName")}</Typography>
        <Typography sx={classes.legalCompanyTagline}>{t("aboutUsCompanyTagline")}</Typography>
        <Box sx={classes.legalDivider} component="hr" />
        {legalRows.map((row) => (
          <Box key={row.labelKey} sx={classes.legalRow}>
            <Typography sx={classes.legalLabel}>{t(row.labelKey)}</Typography>
            <Typography sx={classes.legalValue}>{t(row.valueKey)}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const getContent = () => (
    <Box sx={classes.content}>
      <Box sx={classes.mainContainer}>
        <Box sx={classes.mainBox}>
          <Box sx={classes.subBox}>
            <Container maxWidth="xl">
              {getHeroSection()}
              <Box sx={classes.gradientDivider} component="hr" />
              {getProjectSection()}
              <Box sx={classes.gradientDivider} component="hr" />
              {getCapabilitiesSection()}
              <Box sx={classes.gradientDivider} component="hr" />
              {getLegalSection()}
            </Container>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <AppHeader />
      <Box sx={{ background: "#0000004f" }}>{getContent()}</Box>
      <AppFooter />
    </>
  );
};

export default AboutUs;
